import Joi from 'joi';
import {useCallback, useEffect, useState} from 'react';
import {toast} from 'react-toastify';

import useFilters from '@/hooks/useFilters';
import type {
  CreateSubscriptionPlanDTO,
  UpdateSubscriptionPlanDTO,
} from '@/services/subscriptionPlanService';
import {subscriptionPlanService} from '@/services/subscriptionPlanService';
import type {AdminPaginatedEnvelope, AfriexQueryParams} from '@/types';
import {getResponseMessage} from '@/utils/errorMessage';

import type {AfriexSubscriptionPlan} from './types';

const createSchema = Joi.object({
  name: Joi.string()
    .pattern(/^[A-Za-z\s]+$/)
    .required()
    .label('Plan Name')
    .messages({
      'string.pattern.base': 'Plan Name must contain only letters and spaces',
    }),
  description: Joi.string()
    .pattern(/^[A-Za-z\s]+$/)
    .required()
    .label('Description')
    .messages({
      'string.pattern.base': 'Description must contain only letters and spaces',
    }),
  amount: Joi.number()
    .positive()
    .min(1)
    .max(100)
    .required()
    .label('Amount')
    .messages({
      'number.min': `"Amount" must be between $1 and $100.`,
      'number.max': `"Amount" must be between $1 and $100.`,
    }),
  countries: Joi.array()
    .items(Joi.string().uppercase().trim().required())
    .min(1)
    .required(),
  currency: Joi.string().valid('USD').required().label('Currency'),
  interval: Joi.string().required().label('Interval'),
  features: Joi.array().items(Joi.string()).min(1).required().label('Features'),
  isActive: Joi.boolean().label('Is Active'),
});

const updateSchema = Joi.object({
  name: Joi.string()
    .pattern(/^[A-Za-z\s]+$/)
    .required()
    .label('Plan Name')
    .messages({
      'string.pattern.base': 'Plan Name must contain only letters and spaces',
    }),
  description: Joi.string()
    .pattern(/^[A-Za-z\s]+$/)
    .required()
    .label('Description')
    .messages({
      'string.pattern.base': 'Description must contain only letters and spaces',
    }),
  amount: Joi.number()
    .positive()
    .min(1)
    .max(100)
    .required()
    .label('Amount')
    .messages({
      'number.min': `"Amount" must be between $1 and $100.`,
      'number.max': `"Amount" must be between $1 and $100.`,
    }),
  countries: Joi.array()
    .items(Joi.string().uppercase().trim().required())
    .min(1)
    .required(),
  isActive: Joi.boolean().label('Is Active'),
});

const useSubscriptionPlans = () => {
  const [subscriptionPlanList, setSubscriptionPlanList] =
    useState<AdminPaginatedEnvelope<AfriexSubscriptionPlan[]>>();
  const [subPlanFeatures, setSubPlanFeatures] = useState<string[]>([]);
  const [subPlanIntervals, setSubPlanIntervals] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearch] = useState<boolean>(false);
  const [showPagination] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  const [currentFilters, setCurrentFilters] = useState<AfriexQueryParams>({});
  const [filtersToApply, setFiltersToApply] = useState<AfriexQueryParams>({});
  const [filterCount, setFilterCount] = useState(0);
  const [errors, setErrors] = useState<
    Partial<Record<keyof CreateSubscriptionPlanDTO, string>>
  >({});
  const {applyFilters} = useFilters(setCurrentFilters);

  const fetchAllPlans = useCallback(
    async (params?: AfriexQueryParams) => {
      try {
        setError(null);
        setIsLoading(true);
        const data = await subscriptionPlanService.getSubscriptionPlans({
          ...params,
          ...currentFilters,
        });

        setSubscriptionPlanList(data);
      } catch (error) {
        setError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [currentFilters],
  );

  const createPlan = useCallback(
    async (formData: CreateSubscriptionPlanDTO) => {
      try {
        setError(null);
        setIsLoading(true);
        await subscriptionPlanService.createSubscriptionPlan(formData);
        await fetchAllPlans();
        toast.success('Subscription plan has been created');
      } catch (error) {
        toast.error(
          getResponseMessage(error) ||
            'An Error occured while creating your plan',
        );
        setError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const updatePlan = useCallback(
    async (id: string, formData: UpdateSubscriptionPlanDTO) => {
      try {
        setError(null);
        setIsLoading(true);
        await subscriptionPlanService.updateSubscriptionPlan(id, formData);
        await fetchAllPlans();
        toast.success('Subscription plan has been updated');
      } catch (error) {
        toast.error(
          getResponseMessage(error) ||
            'An Error occured while updating this plan',
        );
        setError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const deletePlan = useCallback(async (id: string) => {
    try {
      setError(null);
      setIsLoading(true);
      await subscriptionPlanService.deleteSubscriptionPlanById(id);
      await fetchAllPlans();
      toast.success('Subscription plan has been deactivated');
    } catch (error) {
      toast.error(
        getResponseMessage(error) ||
          'An Error occured while deleting your plan',
      );
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getFeatures = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);
      const response =
        await subscriptionPlanService.getSubscriptionPlanFeatures();
      setSubPlanFeatures(response);
    } catch (error) {
      toast.error(
        getResponseMessage(error) ||
          'An Error occured while fetching subscription plan features',
      );
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getIntervals = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);
      const response =
        await subscriptionPlanService.getSubscriptionPlanIntervals();
      setSubPlanIntervals(response);
    } catch (error) {
      toast.error(
        getResponseMessage(error) ||
          'An Error occured while fetching subscription plan intervals',
      );
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleCreate = async (form: CreateSubscriptionPlanDTO) => {
    const result = createSchema.validate(form, {abortEarly: false});
    if (result.error) {
      const fieldErrors: Partial<
        Record<keyof CreateSubscriptionPlanDTO, string>
      > = {};
      result.error.details.forEach((detail) => {
        const key = detail.path[0] as keyof CreateSubscriptionPlanDTO;
        fieldErrors[key] = detail.message;
      });
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setIsLoading(true);
    try {
      await createPlan(form);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async ({
    id,
    form,
  }: {
    id: string;
    form: UpdateSubscriptionPlanDTO;
  }) => {
    const result = updateSchema.validate(form, {abortEarly: false});
    if (result.error) {
      const fieldErrors: Partial<
        Record<keyof UpdateSubscriptionPlanDTO, string>
      > = {};
      result.error.details.forEach((detail) => {
        const key = detail.path[0] as keyof UpdateSubscriptionPlanDTO;
        fieldErrors[key] = detail.message;
      });
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setIsLoading(true);
    try {
      await updatePlan(id, form);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Fire-and-forget: all three catch and toast their own failures, so these
    // only stop an unexpected throw becoming an unhandled rejection.
    getFeatures().catch(() => {});
    getIntervals().catch(() => {});
    fetchAllPlans().catch(() => {});
  }, [fetchAllPlans]);

  const handleApplyFilters = useCallback(() => {
    applyFilters(filtersToApply); // Apply the filters
    setIsPageReset((prev) => !prev); // Reset pagination to the first page
  }, [applyFilters, filtersToApply]);

  // Handle change for user manipulation
  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  // Handle dates for applied filters
  const selectAppliedFilters = useCallback(
    (name: string, value: string | number | Date | undefined) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Only count non-undefined values
        const filteredCount = Object.entries(updatedFilters).filter(
          ([_, val]) => val !== undefined,
        ).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
    },
    [],
  );

  const cleanFilters = () => {
    setCurrentFilters({});
    setFiltersToApply({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
  };

  return {
    error,
    fetchAllPlans,
    createPlan,
    updatePlan,
    deletePlan,
    errors,
    handleUpdate,
    handleCreate,
    isLoading,
    isSearch,
    setPage,
    pageInfo: {
      page,
      total: 1,
    },
    showPagination,
    subscriptionPlanList,
    subPlanFeatures,
    subPlanIntervals,
    filterCount,
    isPageReset,
    currentFilters,
    filtersToApply,
    clearFilters: cleanFilters,
    selectAppliedFilters,
    handleApplyFilters,
    selectCurrentFilters,
  };
};

export default useSubscriptionPlans;
